///import store from "../store";

import firebase from "firebase";
import axios from "axios";
import store from "../store";
import Environment from "@/components/EnvironmentInit";
import router from "@/router";
import jQuery from "jquery";
import APIs from "@/components/APIInit";
let $ = jQuery;

const DEEPLINK_SECRET = (typeof process !== "undefined"
	&& process.env
	&& process.env.VUE_APP_E2M_DEEPLINK_SECRET)
	|| "e2m-shared-deeplink-secret"

function base64url(bytes) {
	let bin = ""
	bytes.forEach((b) => (bin += String.fromCharCode(b)))
	return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

async function encryptIdentity(payload, secret = DEEPLINK_SECRET) {
	const enc = new TextEncoder()
	const keyBytes = await crypto.subtle.digest("SHA-256", enc.encode(secret))
	const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-CBC" }, false, ["encrypt"])
	const iv = crypto.getRandomValues(new Uint8Array(16))
	const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-CBC", iv }, key, enc.encode(JSON.stringify(payload))))
	const blob = new Uint8Array(iv.length + ct.length)
	blob.set(iv, 0)
	blob.set(ct, iv.length)
	return base64url(blob)
}

async function buildSecureDeepLink({ channel, token, screenToken, user_id, name, company, designation, roleId, role, instance_id, client_id, event_id, call_doc_id, presence_doc_path, call_setting_doc_path, call_setting_doc_id, chat_doc_path, qna_doc_path, rep_id, call_type, first_name, last_name, timezone, meeting_title, profileImage }) {
	// Both tokens are minted up front and shipped together so the desktop has everything it needs:
	//   * token        → camera/mic publishing on (channel, user_id)
	//   * screen_token → screen-share publishing on (channel, user_id + 1_000_000_000)
	// Mints happen once at handoff; no runtime callback to the OnAir backend from the desktop.
	//
	// user_id is stringified before encryption: the C# IdentityPayload binds user_id to string?,
	// and System.Text.Json throws on JSON number → C# string by default. A throw there would
	// silently null out the whole envelope (token included), so the desktop would report
	// "Token required". Sending as a string sidesteps the issue.

	// Company logo shown in the desktop meeting top bar. Sourced from the current event's branding
	// (EventBranding[0].eventLogo). Read defensively — branding may not be loaded for every flow,
	// in which case the desktop falls back to its built-in default logo.
	let company_logo: string | undefined
	try {
		const branding = (store as any)?.state?.EventBranding
		if (Array.isArray(branding) && branding.length > 0) company_logo = branding[0]?.eventLogo || undefined
	} catch (e) { /* branding not available — desktop uses its default logo */ }

	const data = await encryptIdentity({
		token,
		screen_token: screenToken,
		user_id: String(user_id),
		name,
		company,
		designation,
		roleId,
		role,
		// Context for the desktop's presence-update API call on successful join.
		instance_id,
		client_id: client_id != null ? String(client_id) : undefined,
		event_id,
		call_doc_id: call_doc_id != null ? String(call_doc_id) : undefined,
		presence_doc_path: presence_doc_path != null ? String(presence_doc_path) : undefined,
		call_setting_doc_path: call_setting_doc_path != null ? String(call_setting_doc_path) : undefined,
		call_setting_doc_id: call_setting_doc_id != null ? String(call_setting_doc_id) : undefined,
		chat_doc_path: chat_doc_path != null ? String(chat_doc_path) : undefined,
		qna_doc_path: qna_doc_path != null ? String(qna_doc_path) : undefined,
		rep_id: rep_id != null ? String(rep_id) : undefined,
		call_type: call_type != null ? String(call_type) : undefined,
		first_name,
		last_name,
		timezone,
		meeting_title,
		profile_image: profileImage,
		company_logo,
	})
	return `e2monairmeeting://join?channel=${encodeURIComponent(channel)}&data=${encodeURIComponent(data)}`
}

// Signed NSIS .exe installer. The browser DOWNLOADS it; the user runs it once. After the install we
// CANNOT silently relaunch the app: browsers only allow a custom-protocol (e2monairmeeting://) launch
// from a REAL USER GESTURE, so the open happens when the user clicks "Join Meeting" in the overlay.
// NOTE: upload the produced E2MOnAirMeetSetup.exe to this path.
export const E2M_INSTALLER_URL = "https://firebasestorage.googleapis.com/v0/b/e2monair.firebasestorage.app/o/OA_UAT%2FE2MOnAirMeet%2FE2MOnAirMeetSetup.exe?alt=media"

// Fires an e2monairmeeting:// deep link and, if the desktop app isn't installed, offers the signed
// installer + an overlay whose "Join Meeting" button re-fires the deep link (fresh user gesture) once
// the install finishes. Shared by launchE2MOnAirMeetApp (production) and the /demo-meet page.
export function openDesktopAppOrInstall(deepLink: string): Promise<boolean> {
	const INSTALLER_URL = E2M_INSTALLER_URL
	// How long to wait for an ALREADY-installed app to answer before offering the installer.
	const LAUNCH_TIMEOUT_MS = 5000
	return new Promise((resolve) => {
		let launched = false
		const onHidden = () => { if (document.hidden) { launched = true; cleanup(); resolve(true) } }
		const cleanup = () => {
			document.removeEventListener("visibilitychange", onHidden)
		}
		document.addEventListener("visibilitychange", onHidden)

		// SINGLE protocol trigger via an anchor click — the most reliable single method on modern
		// Chromium/Edge/Firefox because it inherits the user-gesture context (do NOT also fire
		// window.location + iframe; multiple triggers race the MSIX cold-start registration).
		const fireDeepLink = () => {
			try {
				const a = document.createElement('a')
				a.href = deepLink
				a.style.display = 'none'
				document.body.appendChild(a)
				a.click()
				setTimeout(() => { if (a.parentNode) a.parentNode.removeChild(a) }, 500)
			} catch (e) { try { window.location.href = deepLink } catch { /* ignore */ } }
		}
		fireDeepLink()

		setTimeout(() => {
			cleanup()
			if (launched) return
			// App not installed / didn't launch. Offer the signed .exe installer.
			if (!confirm("E2M OnAir Meet desktop app not detected.\n\nClick OK to download the installer. Run it once, then click Join Meeting.")) {
				resolve(false)
				return
			}

			// Phase 1: download the signed installer (.exe).
			window.location.href = INSTALLER_URL

			// Phase 2: keep a loader visible with an always-available "Join Meeting" button — the
			// browser can't detect when the install finishes, and the protocol launch needs a gesture.
			if (!document.getElementById("e2m-launch-style")) {
				const st = document.createElement("style")
				st.id = "e2m-launch-style"
				st.textContent = "@keyframes e2mspin{to{transform:rotate(360deg)}}"
				document.head.appendChild(st)
			}
			const overlay = document.createElement("div")
			overlay.setAttribute("style", "position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;font-family:system-ui,'Segoe UI',Arial,sans-serif")
			const card = document.createElement("div")
			card.setAttribute("style", "background:#fff;border-radius:12px;padding:26px 30px;max-width:440px;text-align:center;box-shadow:0 14px 50px rgba(0,0,0,.35)")
			const spinner = document.createElement("div")
			spinner.setAttribute("style", "width:40px;height:40px;border:4px solid #eee;border-top-color:#EA212D;border-radius:50%;animation:e2mspin .9s linear infinite;margin:0 auto 18px")
			const title = document.createElement("div")
			title.setAttribute("style", "font-size:18px;font-weight:700;margin-bottom:8px;color:#111")
			title.textContent = "Installing E2M OnAir Meet…"
			const subtitle = document.createElement("div")
			subtitle.setAttribute("style", "color:#555;font-size:14px;line-height:1.5;margin-bottom:20px")
			subtitle.innerHTML = "Run the downloaded installer. <b>When it finishes, click Join Meeting.</b>"
			const openBtn = document.createElement("button")
			openBtn.textContent = "Join Meeting"
			openBtn.setAttribute("style", "background:#EA212D;color:#fff;border:0;border-radius:8px;padding:12px 22px;font-size:15px;font-weight:700;cursor:pointer;margin-right:10px")
			const cancelBtn = document.createElement("button")
			cancelBtn.textContent = "Cancel"
			cancelBtn.setAttribute("style", "background:#eee;color:#333;border:0;border-radius:8px;padding:12px 18px;font-size:15px;cursor:pointer")
			card.appendChild(spinner); card.appendChild(title); card.appendChild(subtitle)
			card.appendChild(openBtn); card.appendChild(cancelBtn); overlay.appendChild(card)
			document.body.appendChild(overlay)

			let joinClicked = false
			const teardown = () => {
				document.removeEventListener("visibilitychange", onHidden2)
				if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
			}
			// Treat "tab hidden" as a successful launch ONLY after Join was clicked (otherwise switching
			// to the installer download would look like a launch and close the overlay prematurely).
			const onHidden2 = () => { if (joinClicked && document.hidden) { teardown(); resolve(true) } }
			document.addEventListener("visibilitychange", onHidden2)
			openBtn.addEventListener("click", () => {
				joinClicked = true
				title.textContent = "Opening E2M OnAir Meet…"
				subtitle.innerHTML = "If nothing opens, finish installing and click <b>Join Meeting</b> again."
				fireDeepLink()
			})
			cancelBtn.addEventListener("click", () => { teardown(); resolve(false) })
		}, LAUNCH_TIMEOUT_MS)
	})
}

//var iPadLandscapePlayerSupport = false;
var vm;
export default {
	methods: {
		getCDNUrl(path) {
			if (Environment.Instance.includes('prd')) {
				if (path && path.includes('firebasestorage.googleapis.com')) {
					path = path.replace('https://firebasestorage.googleapis.com/v0/b/e2monair.firebasestorage.app/o/', 'https://resources.e2m.live/');
					//https://resources.e2m.live//firebasestorage.app/OA_UAT%2FDefaults%2Fimage%2FBanner_2048X88_white.jpg?alt=media
					//https://firebasestorage.googleapis.com/v0/b/e2monair.firebasestorage.app/o/OA_UAT_C1649103310659E1649175815465%2FAttendees%2F221000_1757428102486_debashis.png?alt=media
					return path;
				} else {
					return path;
				}
			} else {
				return path;
			}
		},
		getBrandingJSONDetails(path) {
			var url
			if (Environment.Instance.includes('prd')) {
				if (path.includes('EventCustomCSS.json')) {
					// const storage = firebase.storage();
					// const storageRef = storage.ref();
					// url = "https://resources.e2m.live/" + storageRef.bucket + '/' + path;
					url = "https://resources.e2m.live/" + path;
					return axios.get(url);
				} else {
					if (store.getters.getBrandingJsonUrl) {
						return axios.get(store.getters.getBrandingJsonUrl);
					} else {
						// const storage = firebase.storage();
						// const storageRef = storage.ref();
						// url = "https://resources.e2m.live/" + storageRef.bucket + '/' + path;
						url = "https://resources.e2m.live/" + path;
						return axios.get(url);
					}
				}
			} else {
				if (path.includes('EventCustomCSS.json')) {
					const storage = firebase.storage();
					const storageRef = storage.ref();
					// url = "https://resources.e2m.live/" + storageRef.bucket + '/' + path;
					url = "https://firebasestorage.googleapis.com/v0/b/" + storageRef.bucket + '/' + path;
					return axios.get(url);
				} else {
					console.log(store.getters.getBrandingJsonUrl);
					if (store.getters.getBrandingJsonUrl) {
						return axios.get(store.getters.getBrandingJsonUrl);
					} else {
						const storage = firebase.storage();
						const storageRef = storage.ref();
						// url = "https://resources.e2m.live/" + storageRef.bucket + '/' + path;
						url = "https://firebasestorage.googleapis.com/v0/b/" + storageRef.bucket + '/' + path;
						return axios.get(url);
					}
				}
			}
		},
		getCDNUrlOld(path) {
			return path;
			// if (path && path.includes('firebasestorage.googleapis.com')) {
			// 	path = path.replace('https://firebasestorage.googleapis.com/v0/b/', 'https://resources.e2m.live/');
			// 	path = path.replace('/o/', '/');
			// 	return path;
			// } else {
			// 	return path;
			// }
		},
		getBrandingJSONDetailsOld(path) {
			if (path.includes('EventCustomCSS.json')) {
				const storage = firebase.storage();
				const storageRef = storage.ref();
				// var url2 = "https://resources.e2m.live/" + storageRef.bucket + '/' + path;
				var url2 = "https://firebasestorage.googleapis.com/v0/b/" + storageRef.bucket + '/' + path;
				return axios.get(url2);
			} else {
				if (store.getters.getBrandingJsonUrl) {
					return axios.get(store.getters.getBrandingJsonUrl);
				} else {
					const storage = firebase.storage();
					const storageRef = storage.ref();
					// var url = "https://resources.e2m.live/" + storageRef.bucket + '/' + path;
					var url = "https://firebasestorage.googleapis.com/v0/b/" + storageRef.bucket + '/' + path;
					return axios.get(url);
				}
			}
		},
		isMobile() {
			return window.innerWidth < 768;
		},
		isIPad() {
			return window.innerWidth == 768;
		},
		isMobileOrIPad() {
			let result = false;
			if (window.innerWidth < 768) {
				result = true;//"mobile_potrait";
			} else if (window.innerHeight < 570) {
				result = true;//"mobile_landscape";
			} else if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
				result = true;//"ipad_potrait";
			}
			return result;
		},
		verifySupportedDevice(toRouteName, toFullPath) {
			vm = this;
			let result = vm.isMobileOrIPad();
			//alert("verifySupportedDevice: " + toFullPath);
			if (result && ["LiveStage", "LiveStageNG", "StageView"].includes(toRouteName)) {
				//if (iPadLandscapePlayerSupport) {
				store.commit("setRouteName", toFullPath);
				//}				
				setTimeout(() => {
					$("#app").removeClass("player");
					$("body").removeClass("player");
				}, 1000);
				router.push("/screeninfo");
			} else {
				//alert(toRouteName);
				//list of valid path needed
				//store.commit("setRouteName", toFullPath);
			}
		},
		needToShowScreenInfo(toRouteName, toFullPath) {
			vm = this;
			let unsupportedDevice = vm.isMobileOrIPad();
			if (unsupportedDevice && ["LiveStage", "LiveStageNG", "StageView"].includes(toRouteName)) {
				store.commit("setRouteName", toFullPath);
				return true;
			}
			else {
				return false;
			}
		},
		// verifyDeviceAfterResize(toRouteName, toFullPath){
		// 	vm = this;
		// 	//alert(vm.$route.name);
		// 	var lastValidPath = store.getters.getRouteName;
		// 	//list of valid path needed
		// 	alert(lastValidPath);
		// 	router.push(lastValidPath);
		// 	if (["LiveStage", "LiveStageNG", "StageView"].includes(toRouteName)) {

		//       if (self.verifySupportedDevice(toRouteName, toFullPath)) {
		//         // if (from.name) {
		//         //   store.commit("setRouteName", from.name);
		//         // }
		//         //router.push("/screeninfo");
		//       }
		//   }
		// },
		isValidURL: function (url) {
			var urlPattern = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
			//var urlPattern = /^http[s]*\:\/\/[wwW]{3}\.+[a-zA-Z0-9]+\.[a-zA-Z]{2,3}.*$|^http[s]*\:\/\/[^w]{3}[a-zA-Z0-9]+\.[a-zA-Z]{2,3}.*$|http[s]*\:\/\/[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}.*$/;
			var regex = new RegExp(urlPattern);
			return regex.test(url);
		},
		isValidEmail: function (email) {
			var emailPattern = /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
			var regex = new RegExp(emailPattern);
			return regex.test(email);
		},
		currentTime: function (offset = 0) {
			return parseInt((new Date().getTime() / 1000).toString()) - offset;
		},
		AddToEventCalendar(service, title, startTimeSeconds, endTimeSeconds, returnAsLink) {
			let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			var startTimestamp = this.getEventCalendarDateTime(startTimeSeconds);
			var endTimestamp = this.getEventCalendarDateTime(endTimeSeconds);
			let body = "";
			if (service == "yahoo") {
				body = title + ": To Login Go To " + location.origin.toLowerCase();
			}
			else {
				body = title + "<br/>To Login Go To " + location.origin.toLowerCase();
			}
			var linkUrl =
				"start=" +
				startTimestamp +
				"&end=" +
				endTimestamp +
				"&title=" +
				title +
				"&description=" +
				body +
				"&location=&timezone=" +
				timeZone.replace("/", "%2F") +
				"&service=" +
				service;
			linkUrl =
				"https://www.addevent.com/dir/?client=amFHciuoCzziVYGDHmDp105401&" +
				linkUrl;
			if (returnAsLink) {
				return linkUrl;
			}
			else {
				window.open(linkUrl, "_blank");
			}
		},
		getEventCalendarDateTime: function (sec) {
			var dt = new Date(sec * 1000);
			var years = dt.getFullYear();
			var months = dt.getMonth() + 1;
			var days = dt.getDate();
			var hours = dt.getHours();
			var minutes = dt.getMinutes();
			//var seconds = dt.getSeconds();

			var yearStr = years.toString();
			var monthStr = months.toString();
			var dayStr = days.toString();
			var hourStr = hours.toString();
			var minuteStr = minutes.toString();
			//var secondStr = seconds.toString();
			var ampm = "AM";

			if (months < 10) {
				monthStr = "0" + months.toString();
			}
			if (days < 10) {
				dayStr = "0" + days.toString();
			}
			// if (hours < 10) {
			// 	hourStr = "0" + hours.toString();
			// }
			// if (minutes < 10) {
			// 	minuteStr = "0" + minutes.toString();
			// }
			// if (seconds < 10) {
			// 	secondStr = "0" + seconds.toString();
			// }

			if (hours < 12) {
				ampm = "AM";
			}
			else {
				ampm = "PM";
			}
			if (hours > 12) {
				hours -= 12;
			}
			if (hours < 10) {
				hourStr = "0" + hours.toString() + "%3A";
			} else if (hours < 12) {
				hourStr = hours.toString() + "%3A";
			}

			if (minutes < 10) {
				minuteStr = "0" + minutes.toString() + "+";
			} else {
				minuteStr = minutes.toString() + "+";
			}

			var formattedValue = "";
			formattedValue =
				yearStr +
				"%2F" +
				monthStr +
				"%2F" +
				dayStr.toString() +
				"+" + hourStr + minuteStr + ampm;
			//alert(formattedValue);
			return formattedValue;
		},
		LocalhostDomain() {
			return Environment.LocalhostDomain;
		},
		Environment() {
			return Environment.Instance;
		},
		// --- E2M OnAir Meet desktop-app handoff ------------------------------------------------
		// Mint an Agora RTC token for (channel, uid) via the existing OnAir SpeakerTokenApiUrl.
		// Used to hand the desktop client a ready-to-use token in the e2monairmeeting:// deep link.
		async generateE2MAgoraToken(channel, uid) {
			const cfg = APIs.PlayerAPIConfig
			const requestData = {
				app: { id: cfg.AppID.toString(), certificate: cfg.Certificate.toString() },
				channelName: channel.toString(),
				userId: parseInt(uid) || 0,
				validityHours: 24,
			}
			const headers = {
				"Content-Type": "application/json",
				Authorization: "Bearer " + (this as any).getSessionToken(),
			}
			const resp = await axios.post(cfg.SpeakerTokenApiUrl, requestData, { headers })
			const data = resp && resp.data
			if (data && data.status === 0 && data.token) return data.token
			throw new Error("E2M token mint failed: " + JSON.stringify(data))
		},
		// Mint BOTH tokens up front and ship them in the encrypted deep link:
		//   * token        : (channel, user_id)                      — camera + mic publishing
		//   * screen_token : (channel, user_id + 1_000_000_000)      — screen-share publishing
		// The desktop pulls them out of the encrypted payload, uses `token` immediately to join the
		// Agora channel under user_id (so others SEE this user with the correct identity), and
		// keeps `screen_token` around for when they click Share Screen later.
		//
		// Why two: Agora binds each token to a specific (channel, uid) pair. Screen share opens a
		// SECOND Agora connection under a different uid (1_000_000_000 + user_id) so remote clients
		// can tell which video stream is the face cam and which is the screen — that second connection
		// needs its own token.
		//
		// Screen-share mint is wrapped in a try/catch: if it fails, join still proceeds and only the
		// share button errors later.
		async launchE2MOnAirMeetApp({ instance_id, client_id, event_id, call_type, call_doc_id, presence_doc_path, call_setting_doc_path, call_setting_doc_id, chat_doc_path, qna_doc_path, rep_id, channel, uid, first_name, last_name, name, company, designation, roleId, role, timezone, meeting_title, profileImage }) {
			const SHARE_ID_START = 1000000000
			const numUid = parseInt(uid) || 0
			const token = await (this as any).generateE2MAgoraToken(channel, numUid)
			let screenToken = ""
			try { screenToken = await (this as any).generateE2MAgoraToken(channel, SHARE_ID_START + numUid) } catch (e) { /* screen share will fail at use-time if Agora enforces tokens */ }
			// alert("Launching E2M OnAir Meet desktop app...");
			const deepLink = await buildSecureDeepLink({
				channel,
				user_id: numUid,
				token,
				screenToken,
				name,
				company,
				designation,
				roleId,
				role,
				instance_id,
				client_id,
				event_id,
				call_doc_id,
				presence_doc_path,
				call_setting_doc_path,
				call_setting_doc_id,
				chat_doc_path,
				qna_doc_path,
				rep_id,
				call_type,
				first_name,
				last_name,
				timezone,
				meeting_title,
				profileImage,
			})

			// Fire the deep link, falling back to the signed installer if the app isn't installed.
			return openDesktopAppOrInstall(deepLink)
		}
	}
}